import { latitudeAtMercatorY, mercatorY } from './clusterCamera';
import { longitudeSpan } from './globeMode';
import type { ViewportPadding } from './googleSearchMapHandle';
import type { GlobeCamera } from './MapGlobe';
import type { MapEnvelope } from './pins';

const TILE_SIZE = 256;
const MAX_ZOOM = 22;
type ViewSize = { width: number; height: number };

const wrapLongitude = (degrees: number) => ((((degrees + 180) % 360) + 360) % 360) - 180;

const uncoveredSize = (size: ViewSize, padding: ViewportPadding): ViewSize => ({
  width: Math.max(1, size.width - (padding.left ?? 0) - (padding.right ?? 0)),
  height: Math.max(1, size.height - (padding.top ?? 0) - (padding.bottom ?? 0)),
});

/**
 * The camera Google's fitBounds settles on: the envelope as large as it fits
 * in the map beside the padding, centered there.
 */
export const fitEnvelopeCamera = (
  envelope: MapEnvelope,
  size: ViewSize,
  padding: ViewportPadding,
): GlobeCamera => {
  const { width, height } = uncoveredSize(size, padding);
  const north = mercatorY(envelope.north);
  const south = mercatorY(envelope.south);
  const lonSpan = longitudeSpan(envelope);
  const fitZoom = Math.log2(
    Math.min(
      width / (TILE_SIZE * Math.max(lonSpan / 360, Number.EPSILON)),
      height / (TILE_SIZE * Math.max(south - north, Number.EPSILON)),
    ),
  );
  const zoom = Math.max(0, Math.min(MAX_ZOOM, fitZoom));
  // The camera keeps the map's vertical middle, so it sits half the inset
  // difference away from the middle of the envelope.
  const shiftY = ((padding.top ?? 0) - (padding.bottom ?? 0)) / 2 / (TILE_SIZE * 2 ** zoom);
  return {
    center: [
      wrapLongitude(envelope.west + lonSpan / 2),
      latitudeAtMercatorY((north + south) / 2 - shiftY),
    ],
    zoom,
  };
};
